import { Button, Spinner } from 'components/Elements';
import { Notifications } from 'components/Notifications/Notifications';
import { AuthProvider } from 'lib/auth';
import { queryClient } from 'lib/react-query';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter as Router } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCw } from 'lucide-react';

const ErrorFallback = () => {
  return (
    <div
      className="
        w-screen h-screen flex flex-col justify-center items-center px-6
        bg-gradient-to-br from-red-50 via-white to-red-100
        text-center
      "
      role="alert"
      aria-labelledby="error-heading"
      aria-describedby="error-description"
    >
      {/* Animated Icon */}
      <motion.div
        initial={{ opacity: 0, y: -15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-6"
      >
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center shadow-inner">
          <AlertTriangle size={42} className="text-red-600" />
        </div>
      </motion.div>

      {/* Heading */}
      <motion.h2
        id="error-heading"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-2xl font-extrabold text-red-700"
      >
        Something Went Wrong
      </motion.h2>

      {/* Description */}
      <motion.p
        id="error-description"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-gray-700 mt-3 max-w-md"
      >
        We’re unable to load this page correctly. Don’t worry, this is usually easy to fix.
      </motion.p>

      {/* Suggested Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
        className="mt-4 text-gray-600 text-sm max-w-sm"
      >
        Try refreshing the page or clearing your browser data.
      </motion.div>

      {/* Refresh Button */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}>
        <Button
          className="mt-8 px-10 py-4  rounded-full bg-green-700 text-white text-lg font-semibold shadow-lg hover:bg-green-800 transition"
          onClick={() => {
            localStorage.clear();
            window.location.assign(window.location.origin);
          }}
          startIcon={<RefreshCw size={18} />}
        >
          Refresh Page
        </Button>
      </motion.div>

      {/* Support Contact */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="text-sm text-gray-500 mt-6"
      >
        Need assistance? Reach us at{' '}
        <a href="mailto:ashitacreatives@gmail.com" className="text-red-600 font-medium underline">
          ashitacreatives@gmail.com
        </a>
      </motion.p>
    </div>
  );
};

export const AppProvider = ({ children }) => {
  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center w-screen h-screen">
          <Spinner size="xl" />
        </div>
      }
    >
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            {process.env.NODE_ENV !== 'test' && <ReactQueryDevtools />}
            <Notifications />
            <AuthProvider>
              <>
                <Router>{children}</Router>
              </>
            </AuthProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
